import React, { useState, useRef, useCallback, useEffect } from "react";
import { TogglerCircle, Header, Screen, SectionDivider, ToggleBox } from "./RangeToggle.styled";
import { getLanguageLevel, getNumericWeight } from "../../../PagesUser/profiles/Profile.utils";

export const RangeToggle = ({
  name,
  weight,
  onWeightChange,
  category,
  isDisabled,
  width = "100px"
}) => {
  const containerRef = useRef(null);

  const dividerPositions = category === "languages" ? getLanguageLevel() : [1, 2, 3, 4];
  const maxPosition = category === "languages" ? 5 : 4;

  const numericWeight = getNumericWeight(weight, category);
  const initialPosition = Math.max(0, Math.min(maxPosition, numericWeight));

  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const currentNumericWeight = getNumericWeight(weight, category);
    if (onWeightChange && position !== currentNumericWeight) {
      onWeightChange(name, position, category);
    }
  }, [position, name, onWeightChange, weight, category]);

  const handleMouseDown = useCallback(
    (e) => {
      if (isDisabled) return;
      setIsDragging(true);
      e.preventDefault();
    },
    [isDisabled]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const containerWidth = rect.width;
      const segmentWidth = containerWidth / (maxPosition + 1);

      // Calculate which segment we're in (0-maxPosition)
      let newPosition = Math.floor(x / segmentWidth);

      // Clamp between 0 and maxPosition
      newPosition = Math.max(0, Math.min(maxPosition, newPosition));

      setPosition(newPosition);
    },
    [isDragging, maxPosition]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(
    (e) => {
      if (!containerRef.current || isDisabled) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const containerWidth = rect.width;
      const segmentWidth = containerWidth / (maxPosition + 1);

      let newPosition = Math.floor(x / segmentWidth);
      newPosition = Math.max(0, Math.min(maxPosition, newPosition));

      setPosition(newPosition);
    },
    [maxPosition, isDisabled]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const circlePosition = `${(position * 100) / (maxPosition + 1) + 10}%`;

  return (
    <Screen width={width} $isDisabled={isDisabled}>
      <Header>{name}</Header>

      <ToggleBox ref={containerRef} onClick={handleClick}>
        {dividerPositions.map((i) => (
          <SectionDivider key={i} left={`${(i * 100) / (maxPosition + 1)}%`} />
        ))}

        <TogglerCircle
          $isDragging={isDragging}
          left={circlePosition}
          onMouseDown={handleMouseDown}
        />
      </ToggleBox>
    </Screen>
  );
};
